// Escena isométrica del estudio (Fase 9.2): vista general (las cuatro salas con
// su tarjeta) o vista de una sala (más grande y con rótulos de detalle).
import React, { useEffect, useRef } from 'react';
import { IsoProvider, Label, makeProjector } from './iso';
import { AgentStation, BOOTH, ControlRoom, DESK_D, HumanDesk, Lobby, PRESENCE_WORD, Room, WALL_H, vuLevel } from './art';
import { boundsOf } from './layout';
import { agentLights } from '../../hooks/useSupervision';
import { intentText } from '../supervision/labels';

const SCALE = 26;

export default function StudioScene({ data, layout, theme, live, now, view, onOpenRoom, flights, onFlightDone, selected, onSelectStation }) {
  const P = makeProjector(SCALE);
  const overview = view === 'overview';
  const keys = overview ? ['control', 'booths', 'lobby', 'humans'] : [view];
  const lightsOf = id => agentLights(live, id, now);
  const c = theme.colors;

  const b = boundsOf(keys.map(k => layout.rooms[k]), P);
  const m = overview ? { l: 230, r: 230, t: 70, b: 80 } : { l: 10, r: 10, t: 40, b: 50 };
  const vbW = b.w + m.l + m.r;
  const viewBox = [b.x - m.l, b.y - m.t, vbW, b.h + m.t + m.b].map(n => Math.round(n)).join(' ');
  // En la vista de una sala el SVG se amplía: la letra se ajusta al tamaño de la sala
  const fontSize = overview ? 9 : Math.max(8, Math.min(16, vbW / 70));

  const summary = roomSummaries(data, layout, lightsOf);

  const renderRoom = (key) => {
    const r = layout.rooms[key];
    let content = null;
    if (key === 'control') content = layout.controlAgents[0]
      ? (
        <Station label={`${layout.controlAgents[0].name}: ver sus tareas`} onSelect={() => onSelectStation({ kind: 'ai', id: layout.controlAgents[0].id, room: 'control' })}>
          <ControlRoom room={r} agents={layout.controlAgents} lightsOf={lightsOf} />
        </Station>
      )
      : <ControlRoom room={r} agents={layout.controlAgents} lightsOf={lightsOf} />;
    if (key === 'booths') content = [...layout.boothSlots].sort((a, b2) => (a.x + a.y) - (b2.x + b2.y))
      .map(slot => (
        <Station key={slot.agent.id} label={`${slot.agent.name}: ver sus tareas`} onSelect={() => onSelectStation({ kind: 'ai', id: slot.agent.id, room: 'booths' })}>
          <AgentStation x={slot.x} y={slot.y} agent={slot.agent} lights={lightsOf(slot.agent.id)}
            selected={selected?.kind === 'ai' && selected.id === slot.agent.id} />
        </Station>
      ));
    if (key === 'lobby') content = <Lobby room={r} queued={data.queue.count} />;
    if (key === 'humans') content = layout.deskSlots.map(slot => (
      <Station key={slot.human.id} label={`${slot.human.name}: ver sus casos`} onSelect={() => onSelectStation({ kind: 'human', id: slot.human.id, room: 'humans' })}>
        <HumanDesk x={slot.x} y={slot.y} human={slot.human} selected={selected?.kind === 'human' && selected.id === slot.human.id} />
      </Station>
    ));

    const room = <Room x={r.x} y={r.y} w={r.w} d={r.d}>{content}</Room>;
    if (!overview) return <g key={key}>{room}</g>;

    const sm = summary[key];
    const open = () => onOpenRoom(key);
    return (
      <g key={key} className="studio-room" role="button" tabIndex={0}
        aria-label={`${theme.rooms[key].label}: ${sm.line}. Entrar a la sala`}
        onClick={open} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}>
        {room}
        <RoomCard P={P} r={r} roomKey={key} title={theme.rooms[key].label} sm={sm} c={c} />
      </g>
    );
  };

  return (
    <IsoProvider scale={SCALE} theme={theme} detail={!overview}>
      <svg viewBox={viewBox} width="100%" style={{ display: 'block', maxHeight: overview ? '72vh' : '64vh' }}
        role="img" aria-label={overview ? 'Vista general del estudio' : theme.rooms[view].label}>
        {keys.map(renderRoom)}
        {/* Rótulos al final: ningún mueble los tapa */}
        {stationLabels(keys, layout, overview, live, lightsOf).map(l => (
          <g key={l.key}>
            <Label at={l.at} text={l.name} size={fontSize} />
            {l.sub && <Label at={l.at} dy={fontSize * 1.25} text={l.sub} size={fontSize * 0.8} color={c.muted} weight={500} />}
          </g>
        ))}
        {overview && flights.map(fl => {
          const from = layout.positions[fl.from];
          const to = layout.positions[fl.to];
          if (!from || !to) return null;
          return <Flight key={fl.id} from={P(...from)} to={P(...to)} color={fl.color || c.case} onDone={() => onFlightDone(fl.id)} />;
        })}
      </svg>
    </IsoProvider>
  );
}

/** Nombre (y en la vista de sala, un detalle) de cada puesto. */
function stationLabels(keys, layout, overview, live, lightsOf) {
  // En la vista de sala, qué está haciendo cada agente de IA
  const doingText = a => {
    const d = live.doing[a.id];
    const busy = lightsOf(a.id).busy;
    if (!d) return busy ? '● atendiendo' : `En espera · ${a.tasks_30m} tareas en 30 min`;
    return busy ? `● ${intentText(d.intent)}` : `Último: ${intentText(d.intent)}`;
  };
  const out = [];
  if (keys.includes('control')) {
    const r = layout.rooms.control;
    layout.controlAgents.slice(0, 1).forEach(a => out.push({
      key: `c-${a.id}`, at: [r.x + r.w / 2, r.y + 3.55, 0], name: a.name,
      sub: overview ? null : doingText(a),
    }));
  }
  if (keys.includes('booths')) {
    layout.boothSlots.forEach(({ agent: a, x, y }) => out.push({
      key: `b-${a.id}`, at: [x + BOOTH / 2, y + BOOTH + 0.15, 0], name: a.name,
      sub: overview ? null : doingText(a),
    }));
  }
  if (keys.includes('humans')) {
    layout.deskSlots.forEach(({ human: h, x, y }) => out.push({
      key: `h-${h.id}`, at: [x + 1.1, y + DESK_D - 0.05, 0],
      name: overview ? h.name.split(' ')[0] : h.name,
      sub: overview ? null : `${PRESENCE_WORD[h.presence] || ''} · ${h.open_cases} ${h.open_cases === 1 ? 'caso' : 'casos'}`,
    }));
  }
  return out;
}

/** Puesto que se puede seleccionar (clic o Enter) sin entrar/salir de la sala. */
function Station({ label, onSelect, children }) {
  const go = e => { e.stopPropagation(); onSelect(); };
  return (
    <g className="studio-station" role="button" tabIndex={0} aria-label={label}
      onClick={go} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(e); } }}>
      {children}
    </g>
  );
}

/** Resumen por sala para las tarjetas de la vista general. */
function roomSummaries(data, layout, lightsOf) {
  const lead = layout.controlAgents[0];
  const booths = layout.boothSlots.map(s => s.agent);
  const activeBooths = booths.filter(a => { const l = lightsOf(a.id); return l.busy || l.flash; }).length;
  const boothTurns = booths.reduce((n, a) => n + a.turns_15m, 0);
  const boothErrors = booths.reduce((n, a) => n + a.errors_1h, 0);
  const online = data.humans.filter(h => h.presence === 'online').length;
  const paused = data.humans.filter(h => h.presence === 'paused').length;
  const open = data.humans.reduce((n, h) => n + h.open_cases, 0);
  const q = data.queue.count;
  const leadLights = lead ? lightsOf(lead.id) : {};
  return {
    control: {
      line: lead ? `${lead.name} · ${leadLights.busy ? 'procesando' : 'en espera'}` : 'Sin orquestador',
      sub: lead ? `${lead.turns_15m} turnos en 15 min` : '',
      level: lead ? vuLevel(lead.turns_15m, leadLights.busy) : 0,
      alerts: lead ? lead.errors_1h : 0,
    },
    booths: {
      line: `${booths.length} agentes · ${activeBooths} al aire`,
      sub: `${boothTurns} turnos en 15 min`,
      level: Math.min(7, Math.ceil(boothTurns / 4)),
      alerts: boothErrors,
    },
    lobby: {
      line: q ? `${q} ${q === 1 ? 'caso' : 'casos'} en cola` : 'Nadie esperando',
      sub: q && data.queue.oldest_queued_at ? `el más antiguo espera ${waitText(data.queue.oldest_queued_at)}` : 'cola vacía',
      level: Math.min(7, q),
      alerts: 0,
    },
    humans: {
      line: `${online} disponibles · ${paused} en pausa`,
      sub: `${open} ${open === 1 ? 'caso abierto' : 'casos abiertos'}`,
      level: Math.min(7, open),
      alerts: 0,
    },
  };
}

function waitText(d) {
  const m = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
  if (m < 1) return 'menos de 1 min';
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${m % 60} min`;
}

// Tarjeta de sala: cada una en su esquina exterior, para no tapar las demás salas
const CARD_W = 214;
const CARD_H = 58;

function RoomCard({ P, r, roomKey, title, sm, c }) {
  let x, y;
  if (roomKey === 'control') { const [px, py] = P(r.x, r.y, WALL_H + 0.3); x = px - CARD_W / 2; y = py - CARD_H - 8; }
  if (roomKey === 'booths') { const [px, py] = P(r.x + r.w, r.y, 0); x = px + 14; y = py - CARD_H / 2; }
  if (roomKey === 'lobby') { const [px, py] = P(r.x, r.y + r.d, 0); x = px - CARD_W - 14; y = py - CARD_H / 2; }
  if (roomKey === 'humans') { const [px, py] = P(r.x + r.w, r.y + r.d, -0.2); x = px - CARD_W / 2; y = py + 12; }
  return (
    <g transform={`translate(${x.toFixed(1)},${y.toFixed(1)})`} className="studio-card">
      <rect width={CARD_W} height={CARD_H} rx={10} fill={c.cardBg} stroke={c.cardBorder} />
      <text x={12} y={19} fontSize={12.5} fontWeight={800} fill={c.text}>{title}</text>
      <text x={12} y={34} fontSize={10.5} fill={c.text}>{sm.line}</text>
      <text x={12} y={48} fontSize={9.5} fill={c.muted}>{sm.sub}</text>
      <g transform={`translate(${CARD_W - 12 - 7 * 8},${10})`}>
        {c.vu.map((col, i) => <rect key={i} x={i * 8} y={0} width={6} height={6} rx={1} fill={i < sm.level ? col : 'rgba(255,255,255,0.08)'} />)}
      </g>
      {sm.alerts > 0 && (
        <g transform={`translate(${CARD_W - 10},${CARD_H - 24})`}>
          <rect x={-40} y={-1} width={40} height={17} rx={8.5} fill={c.clip} />
          <text x={-20} y={11.5} fontSize={10} fontWeight={800} textAnchor="middle" fill="#fff">⚠ {sm.alerts}</text>
        </g>
      )}
    </g>
  );
}

/** Un caso que viaja entre salas describiendo un arco. */
function Flight({ from, to, color, onDone }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    const mid = [(from[0] + to[0]) / 2, Math.min(from[1], to[1]) - 70];
    const tr = p => `translate(${p[0]}px, ${p[1]}px)`;
    let anim = null;
    if (el && el.animate) {
      anim = el.animate(
        [{ transform: tr(from), opacity: 0 }, { transform: tr(from), opacity: 1, offset: 0.08 },
          { transform: tr(mid), opacity: 1, offset: 0.5 }, { transform: tr(to), opacity: 1, offset: 0.92 }, { transform: tr(to), opacity: 0 }],
        { duration: 1800, easing: 'ease-in-out', fill: 'forwards' });
      anim.onfinish = onDone;
    } else {
      const t = setTimeout(onDone, 50);
      return () => clearTimeout(t);
    }
    return () => anim && anim.cancel();
  }, []); // cada vuelo se anima una sola vez
  return (
    <g ref={ref} style={{ transform: `translate(${from[0]}px, ${from[1]}px)`, opacity: 0, pointerEvents: 'none' }}>
      <circle r={14} fill={color} opacity={0.22} />
      <rect x={-9} y={-6.5} width={18} height={13} rx={2} fill={color} stroke="rgba(0,0,0,0.45)" />
      <path d="M -9 -6.5 L 0 1 L 9 -6.5" fill="none" stroke="rgba(0,0,0,0.5)" strokeWidth={1.3} />
    </g>
  );
}
